import React from 'react';
import { Plus } from 'lucide-react';
import FoodAvatar from './FoodAvatar';
import { formatRecentTime } from '../../utils/logFoodMappers';
import { formatMacro } from '../../utils/nutritionFormat';

/**
 * Vertical list of recently-logged foods. Used on every Log Food tab's
 * landing state (and as a 3-item subsection during search).
 *
 * Props:
 *   items: RecentFood[]
 *   onSelect(recent): tap card body → review/edit in FoodConfirm
 *   onQuickLog(recent): tap [+] → instant re-log toast, no FoodConfirm
 *   title: header label ("Recently Logged")
 *   trailingAction: optional React node rendered right of the title (e.g. "Search all →")
 *   emptyMessage: optional string shown when items is empty
 *   limit: optional max items to render
 */
export default function RecentlyLoggedList(props) {
  const {
    items = [],
    onSelect,
    onQuickLog,
    title = 'Recently Logged',
    trailingAction = null,
    emptyMessage = null,
    limit = null,
  } = props;

  const sliced = limit ? items.slice(0, limit) : items;

  if (sliced.length === 0 && !emptyMessage) {
    return null;
  }

  return (
    <div className="mb-4" data-testid="recently-logged-list">
      <div className="flex items-center justify-between px-4 py-2">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
        </h3>
        {trailingAction}
      </div>
      {sliced.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">{emptyMessage}</div>
      ) : (
        <ul className="divide-y divide-border">
          {sliced.map(function (item) {
            const macros = item.macros || {};
            const time = formatRecentTime(item.last_logged_at);
            const subtitle =
              'P' + formatMacro(macros.protein || 0) +
              ' · C' + formatMacro(macros.carbs || 0) +
              ' · F' + formatMacro(macros.fat || 0) +
              (item.log_count ? ' · ×' + item.log_count : '') +
              (time ? ' · ' + time : '');
            return (
              <li
                key={item.key}
                className="flex items-center gap-3 px-4 py-3 hover:bg-accent/30 transition-colors"
                data-testid="recent-food-row"
              >
                <button
                  type="button"
                  className="flex-1 flex items-center gap-3 text-left min-w-0"
                  onClick={function () { onSelect && onSelect(item); }}
                  data-testid="recent-food-tap"
                >
                  <FoodAvatar item={item} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                      <span className="text-sm font-semibold text-foreground shrink-0">
                        {Math.round(macros.calories || 0)} cal
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
                  </div>
                </button>
                <button
                  type="button"
                  className="shrink-0 size-8 rounded-full bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center transition-colors"
                  onClick={function (e) {
                    e.stopPropagation();
                    onQuickLog && onQuickLog(item);
                  }}
                  aria-label={'Quick log ' + item.name}
                  data-testid="recent-food-quick-log"
                >
                  <Plus className="size-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
